/*
 * @Description: 实时定位驾驶舱 — 「告警记录」弹窗（对齐设计图，参考 ai_device_web 告警记录）
 *   时间段 + 告警类型 + 成员筛选；按日期分组（今天/昨天）；行含类型图标、已恢复标签、时间+昵称+内容。
 *   数据走 D1 /alerts/list（透传 ListAlerts）。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Select, DatePicker } from 'antd'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BellOutlined,
  ClockCircleOutlined,
  DownOutlined,
  PoweroffOutlined,
  RiseOutlined,
  ThunderboltOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useAppDispatch } from '../../../../app/hooks'
import { fetchAlertsList } from '../../../../actions/locationCockpit'
import { isCockpitMockEnabled } from '../../constants'
import { MOCK_ALERT_LIST } from '../../mock/alerts.mock'
import {
  AlertListData,
  AlertListItem,
  CockpitEnvelope
} from '../../types/cockpit.types'
import { DepartmentMemberV2 } from '../../../../global'
import DepartmentMemberPicker, {
  SelectedDepartment
} from '../../../components/DepartmentMemberPickerV2'
import './index.scss'

const { Option } = Select
const { RangePicker } = DatePicker

const PAGE_SIZE = 50

/** 告警类型 → 标签 / 图标 / 配色（rounded 方块图标 + 浅底深色符号） */
const TYPE_META: {
  [key: string]: { label: string; icon: string; color: string; bg: string }
} = {
  stay_timeout: {
    label: '停留时长超限',
    icon: 'clock-circle',
    color: '#EB2F96',
    bg: '#FCE7F0'
  },
  continuous_distance: {
    label: '连续移动超距',
    icon: 'rise',
    color: '#FA8C16',
    bg: '#FFF1E0'
  },
  offline: {
    label: '设备下线',
    icon: 'arrow-down',
    color: '#F5485F',
    bg: '#FFEAEE'
  },
  recovered: {
    label: '设备上线',
    icon: 'arrow-up',
    color: '#52C41A',
    bg: '#E9F8E8'
  },
  low_battery: {
    label: '设备低电量',
    icon: 'battery',
    color: '#FAAD14',
    bg: '#FEF6DC'
  },
  deep_sleep: {
    label: '低电休眠',
    icon: 'poweroff',
    color: '#FAAD14',
    bg: '#FEF6DC'
  },
  ai_anomaly: {
    label: 'AI 异常',
    icon: 'thunderbolt',
    color: '#722ED1',
    bg: '#F1E9FB'
  }
}

/** 类型筛选下拉项 */
const RULE_OPTIONS = [
  { label: '全部告警', value: '' },
  { label: '停留时长超限', value: 'stay_timeout' },
  { label: '连续移动超距', value: 'continuous_distance' },
  { label: '设备下线', value: 'offline' },
  { label: '设备上线', value: 'recovered' },
  { label: '设备低电量', value: 'low_battery' },
  { label: 'AI 异常', value: 'ai_anomaly' }
]

/** 告警类型 icon 字符串 → antd6 组件（battery 走内联 SVG，不在此表） */
const ALERT_ICONS: Record<string, React.ComponentType<any>> = {
  'clock-circle': ClockCircleOutlined,
  rise: RiseOutlined,
  'arrow-down': ArrowDownOutlined,
  'arrow-up': ArrowUpOutlined,
  poweroff: PoweroffOutlined,
  thunderbolt: ThunderboltOutlined,
  bell: BellOutlined
}

/** 类型图标（浅底圆角方块 + 符号；low_battery 用内联电池 SVG，其余用 antd Icon） */
function AlertTypeIcon({ ruleType }: { ruleType: string }) {
  const meta = TYPE_META[ruleType] || {
    icon: 'bell',
    color: '#8c8c8c',
    bg: '#f0f0f0',
    label: ''
  }
  const MetaIcon = ALERT_ICONS[meta.icon] ?? BellOutlined
  return (
    <span
      className="alert-record-row__icon"
      style={{ background: meta.bg, color: meta.color }}
    >
      {meta.icon === 'battery' ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect
            x="2"
            y="8"
            width="16"
            height="9"
            rx="2"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <rect
            x="4.5"
            y="10.5"
            width="7"
            height="4"
            rx="1"
            fill="currentColor"
          />
          <rect
            x="19.5"
            y="11"
            width="2.6"
            height="3"
            rx="1"
            fill="currentColor"
          />
        </svg>
      ) : (
        <MetaIcon />
      )}
    </span>
  )
}

/** 日期分组表头文案：「6月15日 · 今天 / 昨天」 */
function groupHeader(ms: number): string {
  const m = dayjs(ms)
  const label = `${m.month() + 1}月${m.date()}日`
  const key = m.format('YYYY-MM-DD')
  if (key === dayjs().format('YYYY-MM-DD')) return `${label} · 今天`
  if (key === dayjs().subtract(1, 'day').format('YYYY-MM-DD')) {
    return `${label} · 昨天`
  }
  return label
}

interface AlertGroup {
  key: string
  header: string
  items: AlertListItem[]
}

interface AlertRecordModalProps {
  /** 是否显示 */
  visible: boolean
  /** 当前团队长 id */
  groupId: string
  /** 关闭 */
  onClose: () => void
}

const AlertRecordModal: React.FC<AlertRecordModalProps> = ({
  visible,
  groupId,
  onClose
}) => {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()

  // 筛选：时间段（默认本月初至今）/ 类型 / 成员
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    [dayjs().startOf('month'), dayjs().endOf('day')]
  )
  const [ruleType, setRuleType] = useState('')
  const [filterMemberIds, setFilterMemberIds] = useState<string[]>([])
  const [memberPickerOpen, setMemberPickerOpen] = useState(false)
  const [filterEcho, setFilterEcho] = useState<{
    departmentUsers: DepartmentMemberV2[]
    departments: SelectedDepartment[]
  }>({ departmentUsers: [], departments: [] })

  const [items, setItems] = useState<AlertListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const aliveRef = useRef(true)
  const pageRef = useRef(1)
  const loadingMoreRef = useRef(false)

  // 时间段 → unix 秒（清空 = 全部时间）
  const startTime = useMemo(
    () => (dateRange ? dateRange[0].clone().startOf('day').unix() : undefined),
    [dateRange]
  )
  const endTime = useMemo(
    () => (dateRange ? dateRange[1].clone().endOf('day').unix() : undefined),
    [dateRange]
  )

  const load = useCallback(
    (append: boolean) => {
      if (isMock) {
        setItems(MOCK_ALERT_LIST.items)
        setTotal(MOCK_ALERT_LIST.total)
        pageRef.current = 1
        return
      }
      if (!groupId) return
      const pageNo = append ? pageRef.current + 1 : 1
      if (append) {
        loadingMoreRef.current = true
        setLoadingMore(true)
      } else {
        setLoading(true)
      }
      dispatch(
        fetchAlertsList({
          groupId,
          status: 'all',
          ...(filterMemberIds.length ? { userIds: filterMemberIds } : {}),
          ...(ruleType ? { ruleType } : {}),
          ...(startTime ? { startTime } : {}),
          ...(endTime ? { endTime } : {}),
          pageNo,
          pageSize: PAGE_SIZE
        })
      )
        .then((res: CockpitEnvelope<AlertListData>) => {
          if (!aliveRef.current) return
          setLoading(false)
          setLoadingMore(false)
          loadingMoreRef.current = false
          if (res && res.code === 200) {
            const list = res.data?.items ?? []
            setTotal(res.data?.total ?? 0)
            pageRef.current = pageNo
            setItems((prev) => (append ? prev.concat(list) : list))
          }
        })
        .catch(() => {
          if (!aliveRef.current) return
          setLoading(false)
          setLoadingMore(false)
          loadingMoreRef.current = false
        })
    },
    [dispatch, groupId, isMock, filterMemberIds, ruleType, startTime, endTime]
  )

  // 打开 / 筛选变化 → 拉首页
  useEffect(() => {
    if (!visible) return undefined
    aliveRef.current = true
    load(false)
    return () => {
      aliveRef.current = false
    }
  }, [visible, load])

  const hasMore = !isMock && items.length < total
  // 滚动到距底部 80px 内自动加载下一页（不再用「加载更多」按钮）
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (
      hasMore &&
      !loadingMoreRef.current &&
      el.scrollHeight - el.scrollTop - el.clientHeight < 80
    ) {
      load(true)
    }
  }

  // 成员筛选弹窗确定
  const handleMemberFilterOk = useCallback(
    (params: {
      selectedDepartmentUsers: DepartmentMemberV2[]
      selectedDepartments: SelectedDepartment[]
    }) => {
      const { selectedDepartmentUsers, selectedDepartments } = params
      setFilterEcho({
        departmentUsers: selectedDepartmentUsers,
        departments: selectedDepartments
      })
      setFilterMemberIds(selectedDepartmentUsers.map((u) => u.userID))
      setMemberPickerOpen(false)
    },
    []
  )

  // 按日期分组（items 时间倒序 → 分组也按日期倒序）
  const groups = useMemo<AlertGroup[]>(() => {
    const map = new Map<string, AlertGroup>()
    const order: string[] = []
    items.forEach((it) => {
      const key = dayjs(it.triggeredAt).format('YYYY-MM-DD')
      let g = map.get(key)
      if (!g) {
        g = { key, header: groupHeader(it.triggeredAt), items: [] }
        map.set(key, g)
        order.push(key)
      }
      g.items.push(it)
    })
    return order.map((k) => map.get(k) as AlertGroup)
  }, [items])

  return (
    <Modal
      title="告警记录"
      open={visible}
      width={720}
      centered
      footer={null}
      onCancel={onClose}
      className="alert-record-modal"
    >
      <div className="alert-record__filters">
        <RangePicker
          className="alert-record__range"
          value={dateRange || undefined}
          format="MM.DD"
          separator="-"
          allowClear
          suffixIcon={<DownOutlined />}
          disabledDate={(c) => !!c && c > dayjs().endOf('day')}
          onChange={(dates) =>
            setDateRange(
              dates && dates.length === 2
                ? [dates[0] as dayjs.Dayjs, dates[1] as dayjs.Dayjs]
                : null
            )
          }
        />
        <Select
          className="alert-record__type"
          value={ruleType}
          dropdownMatchSelectWidth={false}
          onChange={(v: string) => setRuleType(v)}
        >
          {RULE_OPTIONS.map((o) => (
            <Option key={o.value} value={o.value}>
              {o.label}
            </Option>
          ))}
        </Select>
        <div
          className="alert-record__member"
          onClick={() => setMemberPickerOpen(true)}
        >
          <span
            className={
              filterMemberIds.length
                ? 'alert-record__member-text'
                : 'alert-record__member-text is-placeholder'
            }
          >
            {filterMemberIds.length
              ? `已选 ${filterMemberIds.length} 人`
              : '筛选成员'}
          </span>
          <DownOutlined />
        </div>
      </div>

      <div className="alert-record__list" onScroll={handleScroll}>
        {loading && <div className="alert-record__state">加载中…</div>}
        {!loading && groups.length === 0 && (
          <div className="alert-record__state">暂无告警记录</div>
        )}

        {!loading &&
          groups.map((group) => (
            <div className="alert-record__group" key={group.key}>
              <div className="alert-record__group-head">
                <span className="alert-record__group-date">{group.header}</span>
                <span className="alert-record__group-count">
                  共 {group.items.length} 条
                </span>
              </div>
              {group.items.map((item) => {
                const meta = TYPE_META[item.ruleType]
                const muted =
                  item.ruleType === 'recovered' || item.level === 'info'
                const recovered =
                  !!item.resolvedAt && item.ruleType !== 'recovered'
                const time = dayjs(item.triggeredAt).format('HH:mm')
                return (
                  <div className="alert-record-row" key={item.id}>
                    <AlertTypeIcon ruleType={item.ruleType} />
                    <div className="alert-record-row__main">
                      <div className="alert-record-row__title-line">
                        <span
                          className={`alert-record-row__title ${
                            muted ? 'is-muted' : ''
                          }`}
                        >
                          {(meta && meta.label) || item.title}
                        </span>
                        {recovered && (
                          <span className="alert-record-row__tag">已恢复</span>
                        )}
                      </div>
                      <div className="alert-record-row__desc">
                        {time} {item.userName}，{item.content}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ))}

        {!loading && loadingMore && (
          <div className="alert-record__more">加载中…</div>
        )}
        {!loading && !hasMore && items.length > 0 && (
          <div className="alert-record__more alert-record__more--end">
            没有更多了
          </div>
        )}
      </div>

      <DepartmentMemberPicker
        open={memberPickerOpen}
        title="筛选成员"
        onlyInGroup
        filterObj={{
          departmentUsers: filterEcho.departmentUsers,
          departments: filterEcho.departments
        }}
        onOk={handleMemberFilterOk}
        onCancel={() => setMemberPickerOpen(false)}
      />
    </Modal>
  )
}

export default AlertRecordModal
